import { useCallback, type FormEvent, useEffect, useMemo, useState } from 'react'
import { ExternalLink, Save } from 'lucide-react'
import { adminApi } from '../api'
import RichTextEditor from '../editor/RichTextEditor'
import { Btn, Card, Field, Page, PolicySkeleton, inputClass } from '../ui'
import { policyBodyToHtml } from '../../utils/policyHtml'

const POLICIES = [
  {
    slug: 'privacy',
    label: 'Privacy',
    path: '/privacy',
    blurb: 'How DigiSutra collects, uses, and protects visitor data.',
  },
  {
    slug: 'terms',
    label: 'Terms',
    path: '/terms',
    blurb: 'Website and service terms & conditions.',
  },
  {
    slug: 'refund',
    label: 'Refund',
    path: '/refund',
    blurb: 'Refund / no-return policy for retainers and projects.',
  },
] as const

type Slug = (typeof POLICIES)[number]['slug']

type Meta = { slug: string; title: string; updated_at?: string }

export default function PoliciesPage() {
  const [slug, setSlug] = useState<Slug>('privacy')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [list, setList] = useState<Meta[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const active = useMemo(() => POLICIES.find((p) => p.slug === slug) || POLICIES[0], [slug])

  const loadList = useCallback(async () => {
    const { data } = await adminApi<{ policies?: Meta[] }>('/api/admin/website/policies')
    setList(data.policies || [])
  }, [])

  const load = useCallback(async (s: Slug) => {
    setLoading(true)
    setMsg(null)
    setErr(null)
    const { res, data } = await adminApi<{
      policy?: { title: string; content_html: string; updated_at?: string }
    }>(`/api/admin/website/policies/${s}`)
    if (res.ok && data.policy) {
      setTitle(data.policy.title || '')
      // Normalize plain-text seeds into HTML so TipTap edits cleanly
      setContent(policyBodyToHtml(data.policy.content_html || ''))
      setUpdatedAt(data.policy.updated_at || null)
    } else {
      const meta = POLICIES.find((p) => p.slug === s)
      setTitle(meta?.label === 'Terms' ? 'Terms & Conditions' : `${meta?.label || s} Policy`)
      setContent('')
      setUpdatedAt(null)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    void load(slug)
  }, [slug, load])

  async function save(e: FormEvent) {
    e.preventDefault()
    setMsg(null)
    setErr(null)
    setSaving(true)
    const { res, data } = await adminApi<{ message?: string }>(`/api/admin/website/policies/${slug}`, {
      method: 'PUT',
      json: { title, content_html: content },
    })
    setSaving(false)
    if (!res.ok) {
      setErr(data.message || 'Save failed')
      return
    }
    setMsg('Saved — live on the public site')
    setUpdatedAt(new Date().toISOString())
    void loadList()
  }

  function updatedLabel(s: string) {
    const row = list.find((r) => r.slug === s)
    if (!row?.updated_at) return null
    const d = new Date(row.updated_at)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <Page
      actions={
        <a
          href={active.path}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-xl border border-primary/15 bg-white px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/[0.04]"
        >
          View live <ExternalLink className="h-3.5 w-3.5" />
        </a>
      }
    >
      <Card className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-navy-900">Policy page</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Edit Privacy, Terms, or Refund — content is served from CMS on the public site.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {POLICIES.map((p) => {
            const on = p.slug === slug
            const when = updatedLabel(p.slug)
            return (
              <button
                key={p.slug}
                type="button"
                disabled={loading || saving}
                onClick={() => setSlug(p.slug)}
                className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                  on
                    ? 'border-primary bg-primary text-white shadow-sm'
                    : 'border-primary/15 bg-white text-slate-700 hover:border-primary/35 hover:bg-primary/[0.04]'
                }`}
              >
                {p.label}
                {when && (
                  <span className={`ml-1.5 font-normal ${on ? 'text-white/75' : 'text-slate-400'}`}>
                    {when}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <p className="text-xs text-slate-500">
          <span className="font-medium text-slate-700">{active.label}</span>
          {' · '}
          <code className="rounded bg-slate-100 px-1">{active.path}</code>
          {' · '}
          {active.blurb}
        </p>
      </Card>

      {loading ? (
        <PolicySkeleton />
      ) : (
        <Card>
          <form onSubmit={save} className="space-y-4">
            <Field label="Page title">
              <input
                className={inputClass}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g. Privacy Policy"
              />
            </Field>
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Content</span>
              <RichTextEditor
                value={content}
                onChange={setContent}
                minHeight={420}
                placeholder="Write the full policy…"
              />
              <p className="text-[0.7rem] text-slate-500">
                Use headings for sections. Images upload to CDN. Changes go live after Save.
              </p>
            </div>
            {updatedAt && (
              <p className="text-xs text-slate-500">
                Last saved:{' '}
                {new Date(updatedAt).toLocaleString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
            {msg && <p className="text-sm text-emerald-700">{msg}</p>}
            {err && <p className="text-sm text-red-600">{err}</p>}
            <div className="flex flex-wrap gap-2">
              <Btn type="submit" disabled={saving}>
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : `Save ${active.label}`}
              </Btn>
            </div>
          </form>
        </Card>
      )}
    </Page>
  )
}
